import React, { useEffect, useState } from 'react';

import { useNavigate, useParams, useResolvedPath } from 'react-router-dom';

import Column from '@amzn/meridian/column';
import Link from '@amzn/meridian/link';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import moment from 'moment';
import LoadingSpinner from 'src/components/shared/loadingSpinner/loadingSpinner';
import associateProfileService from 'src/services/associateProfile/associateProfileService';
import type { AssociateProfilePlacementsResponse } from 'src/services/associateProfile/types';
import { useAppSelector } from 'src/store/store';
import type { DataFetchingState } from 'src/types';
import { type RestrictionListParentTask, type RestrictionListTask } from 'src/utils/restrictionUtils';

import ActivePlacementCard from './activePlacementCard';
import PendingPlacementCard from './pendingPlacementCard';
import TransferredInPlacementCard from './transferredInPlacementCard';

type PageState = DataFetchingState<{
  placements: AssociateProfilePlacementsResponse;
}>;

const DEFAULT_PAGE_STATE: PageState = {
  status: 'loading',
} as const;

interface AssociateProfilePlacementsProps {
  regionTasks: (RestrictionListTask | RestrictionListParentTask)[];
}

const AssociateProfilePlacements = ({ regionTasks }: AssociateProfilePlacementsProps) => {
  const { alias } = useParams();
  const { selectedSite } = useAppSelector((state) => state.sites);
  const [pageState, setPageState] = useState<PageState>(DEFAULT_PAGE_STATE);

  useEffect(() => {
    const abortController = new AbortController();

    const fetchAllPageData = async () => {
      setPageState({ status: 'loading' });

      if (!alias || selectedSite.id === 0) {
        if (!abortController.signal.aborted) {
          setPageState({ status: 'error' });
        }
        return;
      }

      try {
        const { data: placements } = await associateProfileService.getAssociateProfilePlacements(
          alias,
          selectedSite.id,
          { signal: abortController.signal },
        );

        if (!abortController.signal.aborted) {
          setPageState({
            status: 'success',
            data: { placements },
          });
        }
      } catch (error) {
        if (!abortController.signal.aborted) {
          console.error('Failed to fetch page data:', error);
          setPageState({ status: 'error' });
        }
      }
    };

    fetchAllPageData();

    return () => {
      abortController.abort();
    };
  }, [alias, selectedSite]);

  if (pageState.status === 'loading') {
    return <LoadingSpinner />;
  }

  if (pageState.status === 'error') {
    return <Text>Oops! Something went wrong.</Text>;
  }

  if (
    pageState.data.placements.pending.length === 0 &&
    pageState.data.placements.active.length === 0 &&
    pageState.data.placements.transferredIn.length === 0
  ) {
    return <NoPlacements alias={alias!} siteId={selectedSite.id} siteName={selectedSite.site} />;
  }

  return (
    <Column spacing="400">
      <Text type="b500">
        Placements Pending Action (
        {pageState.data.placements.pending.length + pageState.data.placements.transferredIn.length})
      </Text>
      {pageState.data.placements.pending.map((placement) => (
        <PendingPlacementCard key={placement.accommodationId} placement={placement} regionTasks={regionTasks} />
      ))}
      {pageState.data.placements.transferredIn.map((placement) => (
        <TransferredInPlacementCard key={placement.accommodationId} placement={placement} />
      ))}
      <Text type="b500">Active Placements ({pageState.data.placements.active.length})</Text>
      {pageState.data.placements.active.map((placement) => (
        <ActivePlacementCard key={placement.accommodationId} placement={placement} regionTasks={regionTasks} />
      ))}
    </Column>
  );
};

const NoPlacements = ({ alias, siteId, siteName }: { alias: string; siteId: number; siteName: string }) => {
  const navigate = useNavigate();
  const [lastPlacementDate, setLastPlacementDate] = useState<string | null>(null);
  const pathObj = useResolvedPath('..');

  useEffect(() => {
    const abortController = new AbortController();
    const fetchLastPlacementDate = async () => {
      try {
        const { data } = await associateProfileService.getLastPlacementDate(alias, siteId, {
          signal: abortController.signal,
        });
        if (!abortController.signal.aborted) {
          setLastPlacementDate(data);
        }
      } catch (error) {
        if (!abortController.signal.aborted) {
          console.error('Failed to fetch last placement date:', error);
        }
      }
    };

    fetchLastPlacementDate();

    return () => {
      abortController.abort();
    };
  }, [alias, siteId]);

  return (
    <Column>
      <Text>There are no placements active or pending action at site: {siteName}</Text>
      {lastPlacementDate !== null && (
        <Row>
          <Text>
            Last placement closed on <strong>{moment(lastPlacementDate).format('MM/DD/YYYY')}</strong>.
          </Text>
          <Link type="secondary" href={`${pathObj.pathname}/history`} onClick={navigate}>
            View History
          </Link>
        </Row>
      )}
    </Column>
  );
};

export default AssociateProfilePlacements;
